import React, { useState, useEffect } from 'react';
import { FaCamera, FaTrash, FaTimes, FaSpinner, FaImage } from 'react-icons/fa';
import { dashboardAPI } from '../../services/dashboardAPI';
import { toast } from 'react-toastify';

const PhotoGallery = ({ onUploadClick }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [photoUrls, setPhotoUrls] = useState({});
  const [loadingImages, setLoadingImages] = useState({});

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const data = await dashboardAPI.getUserPhotos();
      setPhotos(data);
    } catch (error) {
      console.error('Failed to fetch photos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(photoUrls).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []);

  const handleDelete = async (photoId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this photo?')) return;

    setDeleting(photoId);
    try {
      await dashboardAPI.deletePhoto(photoId);
      // Revoke the blob URL
      if (photoUrls[photoId]) {
        URL.revokeObjectURL(photoUrls[photoId]);
      }
      setPhotos(photos.filter((p) => p.id !== photoId));
      setPhotoUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[photoId];
        return newUrls;
      });
      toast.success('Photo deleted');
      if (selectedPhoto?.id === photoId) {
        setSelectedPhoto(null);
      }
    } catch (error) {
      toast.error('Failed to delete photo');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
    });
  };

  // Group photos by week
  const photosByWeek = photos.reduce((acc, photo) => {
    const week = photo.weekNumber;
    if (!acc[week]) acc[week] = [];
    acc[week].push(photo);
    return acc;
  }, {});

  const PhotoThumbnail = ({ photo }) => {
    const url = photoUrls[photo.id];
    const isLoading = loadingImages[photo.id];

    return (
      <div
        onClick={() => setSelectedPhoto(photo)}
        className="relative aspect-square rounded-xl overflow-hidden cursor-pointer group bg-gray-100"
      >
        {isLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <FaSpinner className="animate-spin text-gray-400" />
          </div>
        ) : url ? (
          <img
            src={url}
            alt={`Week ${photo.weekNumber}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FaImage className="text-2xl text-gray-300" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
        <button
          onClick={(e) => handleDelete(photo.id, e)}
          disabled={deleting === photo.id}
          className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
        >
          {deleting === photo.id ? (
            <FaSpinner className="animate-spin" />
          ) : (
            <FaTrash className="text-xs" />
          )}
        </button>
        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
          <p className="text-white text-xs">{formatDate(photo.uploadDate)}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <FaCamera className="text-xl text-purple-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">Progress Photos</h3>
        </div>
        <button
          onClick={onUploadClick}
          className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
        >
          <FaCamera />
          Upload
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <FaSpinner className="animate-spin text-2xl text-gray-400" />
        </div>
      ) : photos.length === 0 ? (
        <div className="text-center py-12 px-4 bg-gray-50 rounded-xl">
          <FaCamera className="text-4xl text-gray-300 mx-auto mb-3" />
          <h4 className="font-semibold text-gray-700 mb-2">No Photos Yet</h4>
          <p className="text-gray-500 text-sm mb-4">
            Start documenting your transformation journey by uploading progress photos.
          </p>
          <button
            onClick={onUploadClick}
            className="inline-flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            <FaCamera />
            Upload First Photo
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(photosByWeek)
            .sort(([a], [b]) => b - a)
            .map(([week, weekPhotos]) => (
              <div key={week}>
                <h4 className="text-sm font-semibold text-gray-500 mb-3">Week {week}</h4>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {weekPhotos.map((photo) => (
                    <PhotoThumbnail key={photo.id} photo={photo} />
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Lightbox Modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 p-3 text-white hover:bg-white/10 rounded-lg"
          >
            <FaTimes className="text-2xl" />
          </button>
          {photoUrls[selectedPhoto.id] ? (
            <img
              src={photoUrls[selectedPhoto.id]}
              alt={`Week ${selectedPhoto.weekNumber}`}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="flex items-center justify-center">
              <FaSpinner className="animate-spin text-4xl text-white" />
            </div>
          )}
          <div className="absolute bottom-4 left-0 right-0 text-center text-white">
            <p className="font-semibold">Week {selectedPhoto.weekNumber}</p>
            <p className="text-sm text-gray-300">{formatDate(selectedPhoto.uploadDate)}</p>
            {selectedPhoto.notes && (
              <p className="text-sm text-gray-400 mt-1">{selectedPhoto.notes}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;
